import { createContext, memo, useContext, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChatSticker } from '../../stickers/ChatStickers.jsx';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import './raid-chat-header.css';

const PinContext = createContext({});
export function RaidChatPinProvider({ room, children }) {
  const pinnedId = room.chatHeader?.pinnedMessage?.id;
  const value = useMemo(() => ({ canDirect: room.canDirect, pinnedId, working: room.chatHeaderWorking, onChange: room.changeChatHeader }), [room.canDirect, pinnedId, room.chatHeaderWorking, room.changeChatHeader]);
  return <PinContext.Provider value={value}>{children}</PinContext.Provider>;
}
export const RaidChatPinAction = memo(function RaidChatPinAction({ messageId, ...props }) {
  const context = useContext(PinContext);
  const { canDirect, pinnedId, working, onChange } = { ...context, ...props };
  if (!canDirect) return null;
  const pinned = pinnedId === messageId;
  return <button type="button" className={`raid-control-pin-action${pinned ? ' is-pinned' : ''}`} disabled={working}
    aria-label={pinned ? '메시지 고정 해제' : '메시지 상단 고정'} title={pinned ? '고정 해제' : '메시지 고정'} aria-pressed={pinned}
    onClick={() => onChange('pin', pinned ? '' : messageId)}><DororaidIcon name="pin" size={13} /></button>;
});

function ChatHeaderText({ text, author = '', collapsible = false }) {
  const bodyRef = useRef(null);
  const contentId = useId();
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const node = bodyRef.current;
    if (!node || !collapsible) return undefined;
    const measure = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(node).lineHeight);
      setOverflowing(node.scrollHeight > lineHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, author, collapsible]);
  return <div className={'raid-control-header-text' + (collapsible ? ' is-collapsible' : '') + (expanded ? ' is-expanded' : '')}>
    <p id={contentId} ref={bodyRef}>{author ? <strong>{author} </strong> : null}{text}</p>
    {collapsible && overflowing ? <button type="button" className="raid-control-header-expand" aria-expanded={expanded} aria-controls={contentId} aria-label={expanded ? '내용 접기' : '내용 펼치기'} title={expanded ? '접기' : '펼치기'} onClick={() => setExpanded(value => !value)}><DororaidIcon name={expanded ? 'chevronUp' : 'chevronDown'} size={16} /></button> : null}
  </div>;
}

export function RaidChatHeader({ room, fullContent = false }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState('notice');
  const header = room.chatHeader || {};
  const pinned = header.pinnedMessage;
  const active = !editing && pinned && (selected === 'pin' || !header.notice) ? 'pin' : 'notice';
  if (!room.canDirect && !header.notice && !pinned && !room.chatHeaderError) return null;
  const save = async event => {
    event.preventDefault();
    if (await room.changeChatHeader('notice', body)) setEditing(false);
  };
  return <aside className="raid-control-chat-notice" aria-label="채팅 공지와 고정 메시지">
    <header><div className="raid-control-notice-tabs">
      {header.notice || pinned ? <>
        {header.notice ? <button type="button" className="raid-control-notice-tab" aria-pressed={active === 'notice'} onClick={() => { setSelected('notice'); setEditing(false); }}>공지</button> : null}
        {pinned ? <button type="button" className="raid-control-notice-tab" aria-pressed={active === 'pin'} onClick={() => { setSelected('pin'); setEditing(false); }}>고정</button> : null}
      </> : <strong>공지 · 고정</strong>}
    </div>{room.canDirect ? <button type="button" disabled={room.chatHeaderWorking}
      onClick={() => { setBody(header.notice || ''); setSelected('notice'); setEditing(value => !value); }}>{editing ? '닫기' : header.notice ? '공지 수정' : '공지 작성'}</button> : null}</header>
    {editing && room.canDirect ? <form onSubmit={save}>
      <textarea aria-label="채팅 공지 내용" maxLength={1000} rows={2} value={body} onChange={event => setBody(event.target.value)} />
      <div><small>빈 내용으로 저장하면 공지를 내립니다.</small><button type="submit" disabled={room.chatHeaderWorking}>{room.chatHeaderWorking ? '저장 중' : '저장'}</button></div>
    </form> : active === 'notice' && header.notice ? <ChatHeaderText key={header.notice} text={header.notice} collapsible={!fullContent} /> : null}
    {!editing && active === 'pin' && pinned ? <div key={pinned.id} className="raid-control-pinned-message">
      {pinned.type === 'sticker' ? <><strong>{pinned.authorNickname}</strong><ChatSticker stickerId={pinned.stickerId} stickerIds={pinned.stickerIds} /></> : <ChatHeaderText key={pinned.body} text={pinned.body || '공유 메시지'} author={pinned.authorNickname} collapsible={!fullContent} />}
      <RaidChatPinAction canDirect={room.canDirect} pinnedId={pinned.id} working={room.chatHeaderWorking} onChange={room.changeChatHeader} messageId={pinned.id} />
    </div> : null}
    {!header.notice && !pinned && !editing && room.canDirect ? <small>답장 옆 핀 아이콘을 누르면 여기에 표시됩니다.</small> : null}
    {room.chatHeaderError ? <p className="raid-control-notice-error" role="alert">{room.chatHeaderError}</p> : null}
  </aside>;
}
